import ChannelTree from '@/data/ChannelTree';

class ChannelInfo {
  name: string;
  pos: number | undefined = undefined;
  enabledForLocales: Array<string> | undefined;
  disabledForLocales: Array<string> | undefined;

  constructor(name: string) {
    this.name = name;
  }

  isChannelAllowed(locale: string): boolean {
    if(this.enabledForLocales !== undefined) {
      return this.enabledForLocales.find(itemLocale => locale.startsWith(itemLocale)) !== undefined;
    }
    return this.disabledForLocales?.find(itemLocale => locale.startsWith(itemLocale)) === undefined;
  }
}

export default class ChannelMapper {

  private locale: string | undefined;
  private channelInfoMap: Map<string, ChannelInfo> | undefined = undefined;
  
  constructor(locale?: string) {
    this.locale = locale;
  }

  public setChannelsInfo(channelsInfoDefinition: string) {
    this.channelInfoMap = this.channelsInfoDefinitionToChannelInfoMap(channelsInfoDefinition);
  }

  public getAllowedChannels(channelTree: ChannelTree): Array<number> {

    const currentLocale = this.locale;

    const _getAllowedChannels = (_channelTree: ChannelTree, allowedChannels: Array<number>) => {
      const channelInfo = this.getChannelInfo(_channelTree);
      if(!channelInfo || !currentLocale || channelInfo.isChannelAllowed(currentLocale)) {
        allowedChannels.push(_channelTree.id);
      } 
      for(const subChannelTree of _channelTree.children) {
        _getAllowedChannels(subChannelTree, allowedChannels);
      }
    }

    const allowedChannels = new Array<number>();
    if(!this.channelInfoMap) return allowedChannels;
    _getAllowedChannels(channelTree, allowedChannels);
    return allowedChannels;
  }

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  public static mapFromObjectWithChildren(channelObj: any, orderedParentChannelNames?: Array<string>): ChannelTree  {  
    
    if(!orderedParentChannelNames) orderedParentChannelNames = [];
    
    const channelTree = ChannelMapper.mapFromObject(channelObj, orderedParentChannelNames);

    const channelTreeName = channelTree.name ? channelTree.name : '';
    if(channelObj.children && channelObj.children.length) {
      for(let i = 0; i < channelObj.children.length; ++i) {
        const childChannelTree = ChannelMapper.mapFromObjectWithChildren(
          channelObj.children[i], [...orderedParentChannelNames, channelTreeName]);
        
        if(childChannelTree) channelTree.children.push(childChannelTree);
      }
    }
    return channelTree;
  }
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  private static mapFromObject(channelObj: any, parentChannelNames: Array<string>): ChannelTree {
    if(!channelObj || !channelObj.id) {
      throw new Error(`Can not map ${channelObj} to channel instance.`);
    };
    const channelTree = new ChannelTree(channelObj.id);
    
    if(channelObj.name) channelTree.name = channelObj.name;
    if(channelObj.description) channelTree.description = channelObj.description;
    if(channelObj.parentId) channelTree.parentId = channelObj.parentId;
    if(channelObj.createdDate) channelTree.createdDate = channelObj.createdDate;
    if(channelObj.modifiedDate) channelTree.modifiedDate = channelObj.modifiedDate;
    channelTree.orderedParentChannelNames = parentChannelNames;
    
    return channelTree;
  }

  public filter(channelTree: ChannelTree) {
    if(!this.channelInfoMap || this.channelInfoMap.size === 0 || !this.locale) {
      return;
    }
    const currentLocale = this.locale;
    
    const removeIds = new Array<number>();
    for(const subChannelTree of channelTree.children) {
      
      const channelInfo = this.getChannelInfo(subChannelTree);
      if(currentLocale && channelInfo && !channelInfo.isChannelAllowed(currentLocale)) {
        removeIds.push(subChannelTree.id);
      }
    }
    channelTree.removeChildren(removeIds);
    for(const subChannelTree of channelTree.children) {
      this.filter(subChannelTree);
    }
  }

  public sort(
      channelTree: ChannelTree,
      compFn: (tree1: ChannelTree, tree2: ChannelTree) => number): void {
    
    if(channelTree.children) {
      channelTree.children.sort(compFn);
    }
    for(const subTree of channelTree.children) {
      this.sort(subTree, compFn);
    }
  }

  private getChannelInfo(channelTree: ChannelTree): ChannelInfo | undefined {
    
    if(!this.channelInfoMap) return undefined;
    
    let channelInfo = this.channelInfoMap.get(channelTree.referenceKey);
    if(channelInfo) return channelInfo;

    if(!channelInfo) channelInfo = this.channelInfoMap.get(channelTree.reducedReferenceKey);
    if(channelInfo) return channelInfo;
    
    return this.channelInfoMap.get(channelTree.reducedAltReferenceKey);
  }

  public channelCompareFunc(tree1: ChannelTree, tree2: ChannelTree): number {

    if(tree2.name.length === 0) return -1;
    if(tree1.name.length === 0) return 1;
    
    const tree1ChannelInfo = this.getChannelInfo(tree1);
    const tree2ChannelInfo = this.getChannelInfo(tree2);

    const tree1ChannelPos = !tree1ChannelInfo || tree1ChannelInfo.pos === undefined
      ? undefined
      : tree1ChannelInfo.pos;

    const tree2ChannelPos = !tree2ChannelInfo || tree2ChannelInfo.pos === undefined
      ? undefined
      : tree2ChannelInfo.pos;

    if(tree2ChannelPos === undefined && tree1ChannelPos !== undefined) return -1;
    if(tree2ChannelPos !== undefined && tree1ChannelPos === undefined) return 1;
    
    if(tree2ChannelPos === undefined && tree1ChannelPos === undefined) {

      return tree1.nameLocalized.localeCompare(tree2.nameLocalized, this.locale);
    }
    return (tree1ChannelPos as number) >= (tree2ChannelPos as number) ? 1 : -1;
  }

  private channelsInfoDefinitionToChannelInfoMap(
    channelsInfoDefinition: string): Map<string, ChannelInfo> {

    const channelInfoMap: Map<string, ChannelInfo> = new Map<string, ChannelInfo>();
    if(channelsInfoDefinition.length == 0) return channelInfoMap;
    
    const lines = channelsInfoDefinition.split(/\r?\n/).filter(line => line.trim().length > 0);
    let lineCounter = 0;
    for(const line of lines) {
      const channelInfo = this.parseChannelsInfoDefinition(line, lineCounter++);
      if(channelInfo !== undefined) {
        channelInfoMap.set(channelInfo.name, channelInfo);
      }
    }
    return channelInfoMap;
  }

  private parseChannelsInfoDefinition(channelsInfoDefinition: string, pos: number): ChannelInfo | undefined  {

    if(!channelsInfoDefinition || channelsInfoDefinition.length === 0) return undefined;
    const tokens = channelsInfoDefinition.split(';');
    const channelInfo = new ChannelInfo(tokens[0].trim());
    channelInfo.pos = pos;
    for(let i = 1; i < tokens.length; ++i) {
      
      let token = tokens[i].trim().replaceAll(' ', '');
      if(token.endsWith(',')) token = token.substr(0, token.length - 1);

      if(token === 'nosort') {
        channelInfo.pos = undefined;
      }
      else if(token.startsWith('e[') && token.endsWith(']')) {
        channelInfo.enabledForLocales = this.parseTokenWithLocales(token);
      }
      else if(token.startsWith('d[') && token.endsWith(']')) {
        channelInfo.disabledForLocales = this.parseTokenWithLocales(token);
      }
    }
    return channelInfo;
  }

  private parseTokenWithLocales(token: string): Array<string> {
    if(token.length === 3) return new Array<string>();
    return token.substring(2, token.length - 1).split(',');
  }

  public getChannelsDefinitionDefault(channelTree: ChannelTree): string {
    const channelDefinitions = new Array<string>();
    const clonedChannelTree = channelTree.clone();
    this.sort(clonedChannelTree, this.channelCompareFunc.bind(this));
    this.fillChannelsDefinition(clonedChannelTree, channelDefinitions);
    return channelDefinitions.join('\n');
  }

  private fillChannelsDefinition(channelTree: ChannelTree, channelDefinitions: Array<string>) {
    
    for(const subChannelTree of channelTree.children) {
      channelDefinitions.push(subChannelTree.referenceKey);
    }
    for(const subChannelTree of channelTree.children) {
      this.fillChannelsDefinition(subChannelTree, channelDefinitions);
    }
  }
}
